<template>
    <ul class="time-table no-bullet" :aria-label="t('departure_times')">
        <li v-for="(hour, i) in hours" :key="i" class="time-list">
            <span class="time-item" :class="[selectedHour.hour === hour.hour ? 'is-active' : '']">
                <span class="visuallyhidden">
                    {{ t('departure_times_between', { minHour: `${hour.hour}h`, maxHour: `${getNextHour(i)}h` }) }}
                </span>
                <strong class="hour" aria-hidden="true">{{ hour.hour }}h</strong>
            </span>
            <ul class="no-bullet minutes-list">
                <template v-if="hour.departures.length != 0">
                    <li
                        v-for="(departure, j) in hour.departures"
                        :key="j"
                        :class="{
                            'is-active': isSelectedHour(departure.depart),
                            'is-end-of-route': isEndOfRoute(departure),
                        }"
                        class="minutes-item time-item"
                        :aria-current="isSelectedHour(departure.depart)"
                        :style="{ color: getDepartureColor(departure) }"
                    >
                        <ScheduleItem
                            tag="button"
                            class="hour"
                            :is-end-of-route="isEndOfRoute(departure)"
                            :departure="departure"
                            :hide-hours="true"
                            :selected="!!isSelectedHour(departure.depart)"
                            @click.native="selectHour(departure.idVoyage)"
                        ></ScheduleItem>
                    </li>
                </template>
                <template v-else>
                    <li class="time-item">
                        <span aria-hidden="true">―</span>
                        <span class="visuallyhidden">
                            {{
                                t('no_departures_between', { minHour: `${hour.hour}h`, maxHour: `${getNextHour(i)}h` })
                            }}
                        </span>
                    </li>
                </template>
            </ul>
        </li>
    </ul>
</template>
<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';

import { format } from '@@/js/utils/dateFns';
import ScheduleItem from '@/shared/ScheduleItem';

const props = defineProps({
    departures: {
        type: Array,
        default: () => [],
    },
    nextDeparture: {
        type: Object,
        default: () => ({}),
    },
    otherHeadsigns: {
        type: Array,
        default: () => [],
    },
});

const emit = defineEmits(['selectHour']);

const { t } = useI18n({
    messages: {
        fr: {
            departure_times: "Liste des temps de passages à l'arrêt",
            departure_times_between: 'Liste des temps de passage entre {minHour} et {maxHour}',
            no_departures_between: "Il n'y aucun passage entre {minHour} et {maxHour}",
        },
        en: {
            departure_times: 'List of departure at this stop',
            departure_times_between: 'List of departure times between {minHour} and {maxHour}',
            no_departures_between: 'There is no departure between {minHour} and {maxHour}',
        },
    },
});

const hours = ref([
    { hour: '05', departures: [] },
    { hour: '06', departures: [] },
    { hour: '07', departures: [] },
    { hour: '08', departures: [] },
    { hour: '09', departures: [] },
    { hour: '10', departures: [] },
    { hour: '11', departures: [] },
    { hour: '12', departures: [] },
    { hour: '13', departures: [] },
    { hour: '14', departures: [] },
    { hour: '15', departures: [] },
    { hour: '16', departures: [] },
    { hour: '17', departures: [] },
    { hour: '18', departures: [] },
    { hour: '19', departures: [] },
    { hour: '20', departures: [] },
    { hour: '21', departures: [] },
    { hour: '22', departures: [] },
    { hour: '23', departures: [] },
    { hour: '00', departures: [] },
    { hour: '01', departures: [] },
    { hour: '02', departures: [] },
    { hour: '03', departures: [] },
    { hour: '04', departures: [] },
]);

const selectedHour = ref({ hour: null, min: null });

function init() {
    hours.value.forEach((hour) => {
        props.departures.forEach((departure) => {
            if (format(departure.depart, 'HH') === hour.hour) {
                hour.departures.push(departure);
            }
        });
    });
    selectedHour.value.hour = format(props.nextDeparture.depart, 'HH');
    selectedHour.value.min = format(props.nextDeparture.depart, 'mm');
}

function isEndOfRoute(departure) {
    const isEndOfRoute = props.otherHeadsigns.find((other) => other.lieuFinDesc === departure.lieuFinDesc);
    return isEndOfRoute !== undefined;
}

function getDepartureColor(departure) {
    const headsign = props.otherHeadsigns.find((other) => other.lieuFinDesc === departure.lieuFinDesc);
    return headsign ? headsign.color : null;
}

function selectHour(idTrip) {
    const departure = props.departures.find((departure) => {
        return departure.idVoyage === idTrip;
    });
    selectedHour.value.hour = format(departure.depart, 'HH');
    selectedHour.value.min = format(departure.depart, 'mm');
    emit('selectHour', idTrip);
}

function isSelectedHour(time) {
    let isSelected = false;
    if (format(time, 'HH') === selectedHour.value.hour && format(time, 'mm') === selectedHour.value.min) {
        isSelected = true;
    }
    return isSelected;
}

function getNextHour(index) {
    if (index === hours.value.length - 1) {
        return hours.value[0].hour;
    }
    return hours.value[index + 1].hour;
}

onMounted(init);
</script>

<style lang="scss" scoped>
.time-table {
    display: none;
    &.is-open {
        display: block;
        z-index: 3;
        position: absolute;
        top: 105px;
        right: 16px;
        width: calc(100% - 16px);
        background-color: $color-black-0;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.24);
        border-radius: 15px;
        overflow: hidden;
        @include breakpoint(medium) {
            position: static;
            max-width: none;
            width: auto;
            background-color: transparent;
            box-shadow: none;
            border-radius: 0;
        }
    }
    @include breakpoint(medium) {
        display: block;
    }
}
.time-list {
    display: flex;
    align-items: center;
    padding: 8px;
    gap: 4px;
    border-bottom: 1px solid $color-black-10;
    &:nth-child(even) {
        background-color: $color-black-2;
    }
    @include breakpoint(medium) {
        min-width: auto;
        border: none;
    }
}
.time-item {
    display: inline-flex;
    position: relative;
    text-align: center;
    width: 34px;
    align-items: center;
    justify-content: center;
    height: 32px;
    @include breakpoint(xlarge) {
        @include rem-typo(18px);
    }
}
.minutes-list {
    display: flex;
    flex-wrap: wrap;
    width: 100%;
    gap: 2px;
}
.minutes-item {
    .hour {
        @include rem-typo(16px);
        display: flex;
        align-items: center;
        line-height: 1.5;
        padding: 0;
        background-color: transparent;
        border: none;
        color: $color-blue-60;
        border-bottom: 1px solid currentColor;
        @include breakpoint(xlarge) {
            @include rem-typo(18px);
        }
        :deep(svg) {
            flex-shrink: 0;
        }
    }
}
.is-active {
    background-color: $color-blue-60;
    flex-shrink: 0;
    border-radius: 20px 6px 6px 20px;
    &.minutes-item {
        border-radius: 6px 20px 20px 6px;
    }
    .hour {
        font-weight: 700;
        color: $color-white;
        border-bottom: 0;
    }
}
.is-end-of-route {
    &::after {
        content: '';
        position: absolute;
        bottom: -4px;
        left: 0;
        width: 100%;
        height: 4px;
        background-color: currentColor;
    }
}
</style>
